import React from 'react';
import { MonoLabel } from '../../Editorial';
import { CoachAvatar } from '../../CoachDetailModal';
import { useEntitlements } from '../../../context/EntitlementsContext';

/* "Coaches" accordion. Lifted verbatim out of MatchDetailModal.jsx — this
   section was already at parity with `coachBlock` in MatchDetailSheet.swift,
   so it keeps its existing `match-coaches__*` classes and styling. */

const EmailGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0066CC" strokeWidth="2" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 6l-10 7L2 6" />
  </svg>
);

const PhoneGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#0066CC" aria-hidden="true">
    <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.57.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.57 1 1 0 01-.25 1.01l-2.2 2.21z" />
  </svg>
);

export function hasContent(detail) {
  return (detail.coaches || []).length > 0;
}

export default function CoachesSection({ detail, onSelectCoach }) {
  const coaches = detail.coaches || [];
  const links = detail.links || {};
  const { has } = useEntitlements();
  // Server-resolved; don't re-derive from missing email/phone — a coach can
  // legitimately have no contact on record while the athlete is Pro.
  const canSeeContact = has('coach_contact_info');
  if (coaches.length === 0) return null;

  return (
    <div className="match-coaches">
      {coaches.map((c) => (
        <div key={`${c.sport_slug}-${c.gender}-${c.name}`} className="match-coaches__block">
          <div>
            <MonoLabel>
              {(c.sport_role === 'secondary' ? 'SECOND SPORT' : 'YOUR SPORT')}
              {' · '}{(c.sport_label || '').toUpperCase()}{c.gender ? ` (${c.gender})` : ''}
            </MonoLabel>
          </div>
          {c.name ? (
            <div className="match-coaches__row">
              <button type="button" className="match-coaches__rowMain" onClick={() => onSelectCoach(c)}>
                <CoachAvatar name={c.name} photoUrl={c.photo_url} size={44} />
                <span className="match-coaches__id">
                  <span className="match-coaches__name">{c.name}</span>
                  <span className="match-coaches__meta">
                    {[c.title, c.sport_label].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </button>
              <span className="match-coaches__actions">
                {canSeeContact ? (
                  <>
                    {c.email && (
                      <a href={`mailto:${c.email}`} aria-label="Email coach"><EmailGlyph /></a>
                    )}
                    {/* A phone_needs_area_code number is an undiallable 7-digit
                        string — the coach modal offers copy instead. */}
                    {c.phone && !c.phone_needs_area_code && (
                      <a href={`tel:${c.phone.replace(/\s/g, '')}`} aria-label="Call coach"><PhoneGlyph /></a>
                    )}
                  </>
                ) : (
                  // Deliberately hedged, not "is part of Pro.": the backend nulls
                  // email/phone/socials identically whether the athlete lacks the
                  // entitlement OR the coach genuinely has none on record
                  // (coach_section.py's `show = row is not None and reveal_contact`)
                  // — the client cannot tell those two cases apart from this
                  // payload, so an unconditional claim would promise a reveal
                  // that upgrading sometimes can't deliver. This still gates
                  // strictly on the entitlement (`!canSeeContact`), never on the
                  // data — it just doesn't over-promise what upgrading gets you.
                  <span className="match-coaches__contactGate">Coach contact info, when we have it, is part of Pro.</span>
                )}
              </span>
            </div>
          ) : (
            <div className="match-coaches__empty">
              <strong>Coaching staff not listed yet</strong>
              <span>We haven&apos;t been able to read this school&apos;s staff directory. It&apos;s still the fastest place to find the coach.</span>
              {(links.athletics_url || links.website_url) && (
                <a href={links.athletics_url || links.website_url} target="_blank" rel="noreferrer">
                  Open athletics site ↗
                </a>
              )}
            </div>
          )}
        </div>
      ))}
      <p className="match-coaches__footnote">
        Only the head coach for each of your sports is listed. Contact rules vary
        by division and sport — check your window before reaching out.
      </p>
    </div>
  );
}
